import { Spin } from 'antd';
import { orderBy } from 'lodash';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TRENDING_FLAG, sortAttAsc, sortAttDesc, sortAttName, sortAttPrice, sortAttRank } from '@common/constants';
import { DISHWASHER_OPTIONS, SortDishwasherBy } from '@common/enums';
import { ApplianceModel, ApplianceSource, ApplianceType } from '@common/models';
import { Filters, SectionSubtitle } from '@core/components';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    DishwasherFilters,
    selectDishwasherSelectedOption,
    selectDishwasherSortBy,
    updateDishwasherFilters,
    updateDishwasherSelectedOption,
} from '@redux/slices/dishwasherSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import SupplyIcon from '../../../../../../public/assets/icons/supply.svg';
import TrashIcon from '../../../../../../public/assets/icons/trash.svg';
import useAppliance from '../appliances/useAppliance';
import DishwasherItem from './DishwasherItem';
import DishwasherOptions from './DishwasherOptions';
import { SortDishwasher } from './dishwasher-sort';
import useDishwasher from './useDishwasher';
import useFilteredDishwashers from './useFilteredDishwashers';

import './dishwasher.scss';

const Dishwasher = (): JSX.Element | null => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const zipcode = useAppSelector(selectZipcode);
    const { isFetchingDishwashers } = useDishwasher();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const dishwasherSortBy = useAppSelector(selectDishwasherSortBy);

    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();

    const dishwasherSelectedOption = useAppSelector(selectDishwasherSelectedOption);

    const [isFilterExpanded, setFilterExpanded] = useState(false);

    const { filteredDishwashers, dishwasherFilters } = useFilteredDishwashers();

    const { applianceTypesInDrawingArea, deleteAllAppliancesByType } = useAppliance();
    const { updateActiveDrawingShape, lastActiveDrawingShape, areThereShapesInTheDrawingArea } =
        useContext(DrawingShapeContext);

    const ref = useRef<HTMLDivElement | null>(null);

    const sortDishwashers = (dishwashers: Array<ApplianceModel>): Array<ApplianceModel> => {
        switch (dishwasherSortBy) {
            case SortDishwasherBy.HighToLow:
                return orderBy(dishwashers, [sortAttPrice, sortAttRank], [sortAttDesc, sortAttAsc]);
            case SortDishwasherBy.LowToHigh:
                return orderBy(dishwashers, [sortAttPrice, sortAttRank], [sortAttAsc, sortAttAsc]);
            case SortDishwasherBy.NameAZ:
                return orderBy(dishwashers, [sortAttName], [sortAttAsc]);
            case SortDishwasherBy.NameZA:
                return orderBy(dishwashers, [sortAttName], [sortAttDesc]);
            case SortDishwasherBy.TrendingNow: {
                const onTrendDishwashers = orderBy(
                    dishwashers.filter((dishwasher: ApplianceModel) =>
                        dishwasher.features.find((item) => item.name.match(TRENDING_FLAG))
                    ),
                    [sortAttRank],
                    [sortAttAsc]
                );

                const otherDishwashers = orderBy(
                    dishwashers.filter(
                        (dishwasher: ApplianceModel) =>
                            !dishwasher.features.find((item) => item.name.match(TRENDING_FLAG)) && dishwasher
                    ),
                    [sortAttRank],
                    [sortAttAsc]
                );

                return [...onTrendDishwashers, ...otherDishwashers];
            }
            default:
                return orderBy(dishwashers, [sortAttRank, sortAttName], [sortAttAsc, sortAttAsc]); // may not have a rank, so also try by name
        }
    };

    // TODO: Fix these functions and deduplicate them to be a generic implementation
    const scrollToLoadedDishwashers = () => {
        const estimatePageElm = document.getElementById('EstimatePage');
        const drawingArea = document.getElementsByClassName('drawing-area')[0];

        if (!estimatePageElm || !drawingArea || !ref || !ref.current) {
            return;
        }

        estimatePageElm.scrollTo({
            top: estimatePageElm.scrollTop + ref.current.getBoundingClientRect().bottom - drawingArea.clientHeight,
        });
    };

    const selectDishwasherOption = (dishwasherOption: DISHWASHER_OPTIONS) => {
        if (dishwasherSelectedOption === dishwasherOption) {
            dispatch(updateDishwasherSelectedOption(undefined));
            return;
        }

        dispatch(updateDishwasherSelectedOption(dishwasherOption));
        if (dishwasherOption === DISHWASHER_OPTIONS.NO_DISHWASHERS) {
            if (applianceTypesInDrawingArea([ApplianceType.DISHWASHER])) {
                setIsModalVisible(true);
            } else {
                dispatch(updateDishwasherSelectedOption(dishwasherOption));
            }
        } else {
            scrollToLoadedDishwashers();
        }
    };

    const onConfirmFromConfirmationModal = () => {
        deleteAllAppliancesByType([ApplianceType.DISHWASHER]);
        setIsModalVisible(false);
        updateActiveDrawingShape(lastActiveDrawingShape);
    };

    const onCancelFromConfirmationModal = () => {
        dispatch(updateDishwasherSelectedOption(DISHWASHER_OPTIONS.BROWSE_DISHWASHERS));
        setIsModalVisible(false);
    };

    const programDishwashers = filteredDishwashers.filter((dishwasher: ApplianceModel) => {
        return dishwasher.productSource === ApplianceSource.PROGRAM;
    });

    const storeDishwashers = filteredDishwashers.filter((dishwasher: ApplianceModel) => {
        return dishwasher.productSource === ApplianceSource.IN_STORE;
    });

    const areDishwashersAvailable = zipcode && selectedStore && selectedColor && areThereShapesInTheDrawingArea;
    if (!areDishwashersAvailable) {
        return null;
    }

    return (
        <div className="dishwasher">
            <SectionSubtitle
                name={t('SCROLLABLE_AREA.DISHWASHER.TITLE')}
                description={t('SCROLLABLE_AREA.DISHWASHER.DESCRIPTION')}
                showLine
            />
            <DishwasherOptions
                ref={ref}
                dishwasherSelectedOption={dishwasherSelectedOption}
                onOptionSelect={selectDishwasherOption}
            />
            {dishwasherSelectedOption === DISHWASHER_OPTIONS.BROWSE_DISHWASHERS && (
                <>
                    <div className="title title-with-sort">
                        {t('SCROLLABLE_AREA.DISHWASHER.BROWSE_DISHWASHER_INFO')}
                        <SortDishwasher />
                    </div>
                </>
            )}
            {filteredDishwashers.length > 0 && !isFetchingDishwashers && (
                <div className={`dishwasher-card-wrapper ${isFilterExpanded ? 'row-direction' : ''}`}>
                    {dishwasherSelectedOption === DISHWASHER_OPTIONS.BROWSE_DISHWASHERS && (
                        <Filters
                            isFilterExpanded={isFilterExpanded}
                            setFilterExpanded={setFilterExpanded}
                            filterOptions={{ ...dishwasherFilters }}
                            updateFilters={(dishwasherFilters: DishwasherFilters) =>
                                dispatch(updateDishwasherFilters(dishwasherFilters))
                            }
                        />
                    )}
                    <div className="dishwasher-group-wrapper">
                        {dishwasherSelectedOption === DISHWASHER_OPTIONS.BROWSE_DISHWASHERS ? (
                            <>
                                {programDishwashers.length ? (
                                    <div className="card-appliance">
                                        {sortDishwashers(programDishwashers).map(
                                            (dishwasher: ApplianceModel, id: number) => {
                                                return <DishwasherItem key={id} dishwasher={dishwasher} />;
                                            }
                                        )}
                                    </div>
                                ) : null}
                                {storeDishwashers.length ? (
                                    <div className="card-appliance">
                                        {sortDishwashers(storeDishwashers).map(
                                            (dishwasher: ApplianceModel, id: number) => {
                                                return <DishwasherItem key={id} dishwasher={dishwasher} />;
                                            }
                                        )}
                                    </div>
                                ) : null}
                            </>
                        ) : null}
                    </div>
                </div>
            )}
            {isFetchingDishwashers && <Spin style={{ fontSize: 24 }} />}
            <ConfirmationDialog
                visible={isModalVisible}
                onCancel={onCancelFromConfirmationModal}
                onConfirm={onConfirmFromConfirmationModal}
                icon={
                    dishwasherSelectedOption === DISHWASHER_OPTIONS.SUPPLY_DISHWASHERS ? <SupplyIcon /> : <TrashIcon />
                }
                title={
                    dishwasherSelectedOption === DISHWASHER_OPTIONS.SUPPLY_DISHWASHERS
                        ? t('SCROLLABLE_AREA.DISHWASHER.SUPPLY_DISHWASHER_TITLE')
                        : t('SCROLLABLE_AREA.DISHWASHER.NO_DISHWASHER_IN_SPACE_TITLE')
                }
                content={
                    dishwasherSelectedOption === DISHWASHER_OPTIONS.SUPPLY_DISHWASHERS
                        ? t('SCROLLABLE_AREA.DISHWASHER.SUPPLY_DISHWASHER_DESCRIPTION')
                        : t('SCROLLABLE_AREA.DISHWASHER.NO_DISHWASHER_IN_SPACE_DESCRIPTION')
                }
            />
        </div>
    );
};

export default Dishwasher;
